<template>
  <div v-loading="loading" class="spt-container">
    <!-- Actions Bar -->
    <div class="spt-actions">
      <el-button
        icon="el-icon-refresh"
        :disabled="loading || creating"
        class="spt-btn"
        @click="loadDetail"
      >
        Tải lại
      </el-button>
      <el-button
        type="primary"
        icon="el-icon-key"
        :loading="creating"
        :disabled="!detail || loading"
        class="spt-btn spt-btn--primary"
        @click="createToken"
      >
        Tạo token mới
      </el-button>
    </div>

    <!-- Notes & Alerts (theo quy chuẩn sk-note) -->
    <div v-if="error" class="sk-note sk-note--danger mb-3">
      <i class="el-icon-error" />
      <span>{{ error }}</span>
    </div>

    <div v-if="detail" class="sk-note mb-3">
      <i class="el-icon-info" />
      <span>Thông tin đăng nhập và khóa bảo mật chỉ hiển thị một phần vì lý do an toàn.</span>
    </div>

    <!-- Panel chi tiết SePay -->
    <div v-if="detail" class="sk-panel spt-panel">
      <div class="sk-panel-head">
        <div class="sk-panel-icon">
          <i class="el-icon-key" />
        </div>
        <div>
          <p class="sk-panel-title">Thông tin cấu hình SePay</p>
          <p class="sk-panel-subtitle">Chi tiết tài khoản, khóa xác thực và địa chỉ API kết nối SePay</p>
        </div>
      </div>

      <el-form label-position="top" class="sk-form-grid">
        <el-form-item v-for="field in fields" :key="field.key" :label="field.label">
          <el-input :value="detail[field.key] || 'Chưa có'" readonly />
        </el-form-item>
        <el-form-item label="Thời hạn token do SePay trả về (giây)">
          <el-input :value="String(detail.time == null ? 0 : detail.time)" readonly />
        </el-form-item>
        <el-form-item label="Cập nhật gần nhất">
          <el-input :value="formatDate(detail.lastModifieDate)" readonly />
        </el-form-item>
      </el-form>
    </div>

    <div v-else-if="!loading && !error" class="sk-note sk-note--warning mb-3">
      <i class="el-icon-warning-outline" />
      <span>Chưa có cấu hình SePay. Vui lòng kiểm tra lại thiết lập hệ thống.</span>
    </div>
  </div>
</template>

<script>
import SePayTokenManagementService from "@/services/SePayTokenManagementService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "SePayTokenDetail",
  data() {
    return {
      loading: false,
      creating: false,
      detail: null,
      error: "",
      fields: [
        { key: "userName", label: "Tên đăng nhập" },
        { key: "passWord", label: "Mật khẩu" },
        { key: "apiKeySePay", label: "API Key SePay" },
        { key: "accessToken", label: "Access Token" },
        { key: "secretKey", label: "Secret Key" },
        { key: "sePayBaseUrl", label: "Địa chỉ API SePay" },
        { key: "sePayBankHubBaseUrl", label: "Địa chỉ API Bank Hub" }
      ]
    };
  },
  mounted() {
    this.loadDetail();
  },
  methods: {
    errorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async loadDetail() {
      this.loading = true;
      this.error = "";
      try {
        const response = await SePayTokenManagementService.getDetail();
        this.detail = response.data.data;
      } catch (error) {
        this.detail = null;
        this.error = this.errorMessage(error, "Không thể tải thông tin SePay. Vui lòng thử lại.");
        NotifyMessage.leftCornerNotificationError(this.error);
      } finally {
        this.loading = false;
      }
    },
    async createToken() {
      if (this.creating || this.loading || !this.detail) return;
      try {
        await this.$confirm(
          "Tạo token mới sẽ cập nhật token đang dùng cho kết nối SePay. Bạn muốn tiếp tục?",
          "Xác nhận tạo token mới",
          {
            confirmButtonText: "Tạo token",
            cancelButtonText: "Hủy",
            type: "warning"
          }
        );
      } catch (_) {
        return;
      }
      this.creating = true;
      try {
        await SePayTokenManagementService.createTokenV2();
        NotifyMessage.leftCornerNotificationSuccess("Tạo token SePay thành công");
        await this.loadDetail();
      } catch (error) {
        NotifyMessage.leftCornerNotificationError(
          this.errorMessage(error, "Tạo token SePay thất bại. Vui lòng thử lại.")
        );
      } finally {
        this.creating = false;
      }
    },
    formatDate(value) {
      return value ? value.replace("T", " ").substring(0, 19) : "Chưa có";
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

.spt-container {
  padding: 4px 0 12px;
}

.spt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.spt-btn {
  border-radius: 10px;
  font-weight: 600;
}

.spt-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  padding: 20px;
}

/deep/ .sk-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .el-form-item {
    margin-bottom: 0;
  }

  .el-form-item__label {
    font-size: 13px;
    color: #475569;
    font-weight: 600;
    line-height: 1.5;
    padding-bottom: 4px;
  }

  .el-input__inner {
    border-radius: 10px;
    background-color: #f8fafc;
    color: #1e293b;
    font-weight: 500;
    border-color: #e2e8f0;
  }
}
</style>
